import { parse as parseYaml } from "yaml";

/** The dreamer's per-brain process config, read from `.m8t/brain.yaml`
 *  `processes.dreamer`. This module NEVER writes brain.yaml — it is read-only
 *  and parses defensively (any malformed/absent block ⇒ disabled). */
export type DreamCadence = "nightly" | "off";
export interface DreamerConfig {
  enabled: boolean;
  cadence: DreamCadence;
  model: string;
}

/** Default dreaming model — best Tier-1 deployment (cap 1000). */
export const DEFAULT_DREAM_MODEL = "gpt-5.4";

function asCadence(v: unknown): DreamCadence {
  return v === "nightly" ? "nightly" : "off";
}

/**
 * Parse `processes.dreamer.{enabled,cadence,model}` from a brain.yaml string.
 * Defensive: malformed YAML, an absent/empty block, or wrong-typed fields all
 * degrade to a safe disabled/off/default config rather than throwing. The env
 * `M8T_DREAM_MODEL` overrides the yaml `model` (admin knob).
 */
export function parseDreamerConfig(rawYaml: string, env: NodeJS.ProcessEnv): DreamerConfig {
  let parsed: unknown;
  try {
    parsed = parseYaml(rawYaml);
  } catch {
    return { enabled: false, cadence: "off", model: env.M8T_DREAM_MODEL ?? DEFAULT_DREAM_MODEL };
  }
  const dreamer =
    parsed && typeof parsed === "object"
      ? ((parsed as { processes?: { dreamer?: Record<string, unknown> } }).processes?.dreamer ?? {})
      : {};
  const yamlModel = typeof dreamer.model === "string" ? dreamer.model : DEFAULT_DREAM_MODEL;
  return {
    enabled: dreamer.enabled === true,
    cadence: asCadence(dreamer.cadence),
    model: env.M8T_DREAM_MODEL ?? yamlModel,
  };
}
